import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { api, type Team, type CoachInfo, type InviteActiveResponse, type GenerateInviteLinkResponse } from '../../services/api';

@customElement('team-invite-manager')
export class TeamInviteManager extends LitElement {
  createRenderRoot() {
    return this;
  }

  @state()
  private teams: Team[] = [];

  @state()
  private selectedTeamId = '';

  @state()
  private activeInvite: InviteActiveResponse | null = null;

  @state()
  private coaches: CoachInfo[] = [];

  @state()
  private loading = false;

  @state()
  private generating = false;

  @state()
  private copied = false;

  @state()
  private error = '';

  connectedCallback() {
    super.connectedCallback();
    this.loadTeams();
  }

  private async loadTeams() {
    try {
      this.teams = (await api.getTeams()) || [];
    } catch (error) {
      console.error('Failed to load teams:', error);
    }
  }

  private async handleTeamChange(e: Event) {
    this.selectedTeamId = (e.target as HTMLSelectElement).value;
    this.activeInvite = null;
    this.coaches = [];
    this.error = '';
    this.copied = false;

    if (this.selectedTeamId) {
      this.loading = true;
      try {
        const [invite, coaches] = await Promise.all([
          api.getTeamInvite(this.selectedTeamId),
          api.getTeamCoaches(this.selectedTeamId),
        ]);
        this.activeInvite = invite;
        this.coaches = coaches || [];
      } catch (error) {
        console.error('Failed to load team invite data:', error);
        this.error = 'Failed to load invite data.';
      } finally {
        this.loading = false;
      }
    }
  }

  private async handleGenerate() {
    if (!this.selectedTeamId || this.generating) return;

    this.generating = true;
    this.error = '';

    try {
      const result: GenerateInviteLinkResponse = await api.createTeamInvite(this.selectedTeamId);
      this.activeInvite = {
        active: true,
        token: result.token,
        expires_at: result.expires_at,
        url: result.url,
      };
    } catch (error) {
      console.error('Failed to generate invite:', error);
      this.error = 'Failed to generate invite link.';
    } finally {
      this.generating = false;
    }
  }

  private async handleRevoke() {
    if (!this.selectedTeamId) return;

    try {
      await api.revokeTeamInvite(this.selectedTeamId);
      this.activeInvite = { active: false };
    } catch (error) {
      console.error('Failed to revoke invite:', error);
      this.error = 'Failed to revoke invite link.';
    }
  }

  private async handleCopy() {
    if (!this.activeInvite?.url) return;

    try {
      await navigator.clipboard.writeText(this.activeInvite.url);
      this.copied = true;
      setTimeout(() => { this.copied = false; }, 2000);
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = this.activeInvite.url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      this.copied = true;
      setTimeout(() => { this.copied = false; }, 2000);
    }
  }

  private async handleRemoveCoach(userId: string) {
    if (!this.selectedTeamId) return;

    try {
      await api.removeTeamCoach(this.selectedTeamId, userId);
      this.coaches = this.coaches.filter(c => c.user_id !== userId);
    } catch (error) {
      console.error('Failed to remove coach:', error);
      this.error = 'Failed to remove coach.';
    }
  }

  render() {
    return html`
      <div class="space-y-6">
        <!-- Team selector -->
        <div class="form-control w-full max-w-xs">
          <label class="label">
            <span class="label-text">Select Team</span>
          </label>
          <select
            class="select select-bordered"
            .value=${this.selectedTeamId}
            @change=${this.handleTeamChange}
          >
            <option value="">Choose a team...</option>
            ${this.teams.map(team => html`
              <option value=${team.id}>${team.name}</option>
            `)}
          </select>
        </div>

        ${this.loading ? html`
          <div class="flex items-center gap-2">
            <span class="loading loading-spinner loading-sm"></span>
            <span class="text-sm text-base-content/70">Loading...</span>
          </div>
        ` : nothing}

        ${this.error ? html`
          <div class="alert alert-error text-sm">
            <span>${this.error}</span>
          </div>
        ` : nothing}

        ${this.selectedTeamId && !this.loading ? this.renderInviteSection() : nothing}
        ${this.selectedTeamId && !this.loading ? this.renderCoachesList() : nothing}
      </div>
    `;
  }

  private renderInviteSection() {
    return html`
      <div class="card bg-base-100 shadow-sm">
        <div class="card-body">
          <h3 class="card-title text-base">Invite Link</h3>

          ${this.activeInvite?.active ? html`
            <div class="space-y-3">
              <div class="flex items-center gap-2">
                <input
                  type="text"
                  class="input input-bordered input-sm flex-1 font-mono text-xs"
                  .value=${this.activeInvite.url || ''}
                  readonly
                />
                <button
                  class="btn btn-sm ${this.copied ? 'btn-success' : 'btn-primary'}"
                  @click=${this.handleCopy}
                >
                  ${this.copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <div class="flex items-center justify-between">
                <span class="text-xs text-base-content/60">
                  Expires ${this.activeInvite.expires_at ? new Date(this.activeInvite.expires_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                </span>
                <button
                  class="btn btn-ghost btn-xs text-error"
                  @click=${this.handleRevoke}
                >
                  Revoke
                </button>
              </div>
            </div>
          ` : html`
            <p class="text-sm text-base-content/60 mb-3">
              Generate a shareable link for coaches to join this team.
              The link is valid for 7 days.
            </p>
            <button
              class="btn btn-primary btn-sm"
              @click=${this.handleGenerate}
              ?disabled=${this.generating}
            >
              ${this.generating
                ? html`<span class="loading loading-spinner loading-sm"></span>`
                : 'Generate Invite Link'}
            </button>
          `}
        </div>
      </div>
    `;
  }

  private renderCoachesList() {
    return html`
      <div class="card bg-base-100 shadow-sm">
        <div class="card-body">
          <h3 class="card-title text-base">
            Coaches
            ${this.coaches.length > 0 ? html`<span class="badge badge-sm">${this.coaches.length}</span>` : nothing}
          </h3>

          ${this.coaches.length === 0 ? html`
            <p class="text-sm text-base-content/60">No coaches assigned to this team yet.</p>
          ` : html`
            <div class="overflow-x-auto">
              <table class="table table-sm">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Joined</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  ${this.coaches.map(coach => html`
                    <tr>
                      <td>${coach.display_name}</td>
                      <td class="text-xs">${coach.email}</td>
                      <td class="text-xs">${new Date(coach.assigned_at).toLocaleDateString()}</td>
                      <td>
                        <button
                          class="btn btn-ghost btn-xs text-error"
                          @click=${() => this.handleRemoveCoach(coach.user_id)}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  `)}
                </tbody>
              </table>
            </div>
          `}
        </div>
      </div>
    `;
  }
}
